'use client'

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { ApolloClient, useApolloClient } from '@apollo/client'
import { toast } from 'react-toastify'
import { useAppSelector } from 'features/hooks'
import { RootState } from 'features/store'
import { isUserAvailable } from 'helpers/authUser'
import { canUseShopping } from '@/lib/shoppingLists/access'
import { formatQuantity } from '@/lib/shoppingLists/catalog'
import {
  ACCEPT_SHOPPING_LIST_INVITE,
  ADD_RECIPE_TO_SHOPPING_LIST,
  ADD_SHOPPING_ITEM,
  BULK_UPDATE_SHOPPING_ITEMS,
  CLEAR_SHOPPING_LIST_MARKS,
  CREATE_SHOPPING_LIST,
  CREATE_SHOPPING_LIST_INVITE,
  CREATE_SHOPPING_LIST_WITH_RECIPE,
  DELETE_SHOPPING_LIST,
  DISMISS_SHOPPING_SUGGESTION,
  MERGE_SHOPPING_ITEMS,
  MY_SHOPPING_LISTS,
  REMOVE_RECIPE_FROM_SHOPPING_LIST,
  REMOVE_SHOPPING_ITEM,
  REMOVE_SHOPPING_LIST_MEMBER,
  RESET_SHOPPING_LIST_INVITE,
  REORDER_SHOPPING_ITEMS,
  RENAME_SHOPPING_LIST,
  SHOPPING_RECIPE_OVERLAPS,
  SHOPPING_UNITS_QUERY,
  SPLIT_SHOPPING_ITEM,
  UPDATE_SHOPPING_ITEM,
} from '@/lib/shoppingLists/operations'
import type {
  RecipePayload,
  ShoppingCategory,
  ShoppingItem,
  ShoppingList,
  ShoppingOverlap,
  ShoppingUnit,
  ShoppingUnitOption,
} from '@/lib/shoppingLists/types'

type ItemPatch = Partial<
  Pick<ShoppingItem, 'name' | 'quantity' | 'unit' | 'category' | 'purchased'>
>

type ShoppingListsContextValue = {
  lists: ShoppingList[]
  hydrated: boolean
  totalItemCount: number
  units: ShoppingUnitOption[]
  refresh: (options?: { silent?: boolean }) => Promise<void>
  getList: (id: string) => ShoppingList | undefined
  createList: (name: string) => Promise<ShoppingList | null>
  renameList: (id: string, name: string) => Promise<void>
  deleteList: (id: string) => Promise<void>
  addItem: (
    listId: string,
    input: { name: string; quantity: string; unit: ShoppingUnit }
  ) => Promise<boolean>
  updateItem: (listId: string, itemId: string, patch: ItemPatch) => Promise<void>
  togglePurchased: (listId: string, itemId: string) => Promise<void>
  removeItem: (listId: string, itemId: string) => Promise<void>
  clearPurchased: (listId: string) => Promise<void>
  moveItems: (fromListId: string, toListId: string, itemIds: string[]) => Promise<boolean>
  // `combined` = ingredients merged into a line already on the list;
  // `redirected` = ingredients added to an item on another list, by choice.
  addRecipeToList: (
    listId: string,
    recipe: RecipePayload,
    redirects?: RecipeRedirect[]
  ) => Promise<AddRecipeResult>
  createListWithRecipe: (
    name: string,
    recipe: RecipePayload,
    redirects?: RecipeRedirect[]
  ) => Promise<ShoppingList | null>
  // Ingredients of this recipe already on the person's other lists.
  getOverlaps: (recipeId: string, excludeListId?: string) => Promise<ShoppingOverlap[]>
  // Combining: confirm a suggested merge, undo a combine, or drop a suggestion.
  mergeItems: (listId: string, itemIds: string[]) => Promise<boolean>
  splitItem: (listId: string, itemId: string) => Promise<void>
  dismissSuggestion: (listId: string, itemId: string, otherItemId: string) => Promise<void>
  resetQuantity: (listId: string, itemId: string) => Promise<void>
  removeRecipe: (listId: string, recipeId: string, itemIds?: string[]) => Promise<void>
  // Drag and drop: update a section locally while dragging, then save its order.
  setSectionItems: (listId: string, category: ShoppingCategory, items: ShoppingItem[]) => void
  saveSectionOrder: (listId: string, category: ShoppingCategory) => Promise<void>
  // Sharing: the owner makes (or resets) the invite link; anyone with it joins from the invite page.
  getInviteToken: (listId: string) => Promise<string | null>
  resetInviteToken: (listId: string) => Promise<string | null>
  acceptInvite: (token: string, onSessionExpired?: () => void) => Promise<ShoppingList | null>
  // The owner removes a person; a person removes themselves to leave.
  removeMember: (listId: string, memberId: string) => Promise<boolean>
}

export type RecipeRedirect = { recipeIngredientId: number; itemId: number }
export type AddRecipeResult = { ok: boolean; combined: number; redirected: number }

const ShoppingListsContext = createContext<ShoppingListsContextValue | null>(null)

const unique = <T,>(values: T[]) => Array.from(new Set(values))

// Counts mutations still on their way to the server, so a background refresh of
// shared lists never lands on top of a change the person just made.
let mutationsInFlight = 0
function mutate(client: ApolloClient<object>, options: any) {
  mutationsInFlight += 1
  return client.mutate<any>(options).finally(() => {
    mutationsInFlight -= 1
  })
}

// How often a list that other people can change is re-read while the page is open.
const LIVE_REFRESH_MS = 12000

function fromApiItem(item: any): ShoppingItem {
  const unit =
    item.unit_of_measurement?.unit_abbreviation ||
    item.unit_of_measurement?.unit_name ||
    ''
  const sources: any[] = item.sources ?? []
  const names = unique(
    sources.map((source) => source.recipe_name).filter(Boolean) as string[]
  )
  return {
    id: String(item.item_id),
    name: item.item_name,
    quantity: item.quantity ?? '',
    unit,
    category: item.category as ShoppingCategory,
    purchased: !!item.is_checked,
    recipeIds: unique(
      sources
        .filter((source) => source.recipe_id != null)
        .map((source) => String(source.recipe_id))
    ),
    recipeName: names.length ? names.join(', ') : undefined,
    sources: sources.map((source) => ({
      name: source.source_name ?? undefined,
      recipeId: source.recipe_id != null ? String(source.recipe_id) : undefined,
      recipeName: source.recipe_name ?? undefined,
      amount: [source.quantity_string, source.unit_label].filter(Boolean).join(' '),
    })),
    quantityEdited: !!item.quantity_edited,
    similarIds: (item.similar_item_ids ?? []).map(String),
  }
}

// How many ingredients of this recipe landed on a line that now has more than one source.
function countCombined(apiList: any, recipeId: string) {
  return (apiList?.items ?? []).filter((item: any) => {
    const sources: any[] = item.sources ?? []
    return (
      sources.length > 1 && sources.some((source) => String(source.recipe_id) === recipeId)
    )
  }).length
}

function fromApiList(list: any): ShoppingList {
  return {
    id: String(list.list_id),
    name: list.list_name,
    ownerName: list.owner_name ?? '',
    isOwner: !!list.is_owner,
    members: (list.collaborators ?? [])
      .filter((member: any) => member.status_id === 2)
      .map((member: any) => ({
        id: String(member.collaborator_id),
        userId: member.user_id ?? undefined,
        name: member.display_name || 'Someone',
      })),
    createdAt: list.date_created ?? new Date().toISOString(),
    updatedAt: list.date_updated ?? list.date_created ?? new Date().toISOString(),
    items: (list.items ?? []).map(fromApiItem),
    recipes: (list.recipes ?? []).map((recipe: any) => ({
      id: String(recipe.recipe_id),
      name: recipe.name,
      slug: recipe.slug ?? undefined,
    })),
  }
}

function errorMessage(error: unknown, fallback: string) {
  const message = (error as { message?: string })?.message
  return message && message.length < 140 ? message : fallback
}

const touch = (list: ShoppingList): ShoppingList => ({
  ...list,
  updatedAt: new Date().toISOString(),
})

export function ShoppingListsProvider({ children }: { children: React.ReactNode }) {
  const client = useApolloClient()
  const user = useAppSelector((state: RootState) => state.culineerAuth.user)
  // Farm and market accounts don't get shopping lists, so nothing is loaded for them.
  const signedIn = !!isUserAvailable(user) && canUseShopping(user)

  const [lists, setLists] = useState<ShoppingList[]>([])
  const [units, setUnits] = useState<ShoppingUnitOption[]>([])
  const [hydrated, setHydrated] = useState(false)
  const listsRef = useRef<ShoppingList[]>([])
  listsRef.current = lists

  const draggingRef = useRef(false)

  const refresh = useCallback(
    async (options?: { silent?: boolean }) => {
      try {
        const { data } = await client.query({
          query: MY_SHOPPING_LISTS,
          fetchPolicy: 'network-only',
        })
        // A background refresh must not overwrite a change or drag that is still being saved.
        if (options?.silent && (mutationsInFlight > 0 || draggingRef.current)) return
        setLists((data?.myShoppingLists ?? []).map(fromApiList))
      } catch (error) {
        if (!options?.silent) toast.error(errorMessage(error, 'Could not load your shopping lists'))
      }
    },
    [client]
  )

  // Live sharing: while the page is open, re-read the lists that other people can change,
  // and again whenever the person comes back to the tab.
  const anyShared = lists.some((list) => !list.isOwner || list.members.length > 0)
  useEffect(() => {
    if (!signedIn) return
    const tick = () => {
      if (document.visibilityState === 'visible') refresh({ silent: true })
    }
    document.addEventListener('visibilitychange', tick)
    const timer = anyShared ? window.setInterval(tick, LIVE_REFRESH_MS) : undefined
    return () => {
      document.removeEventListener('visibilitychange', tick)
      if (timer) window.clearInterval(timer)
    }
  }, [signedIn, anyShared, refresh])

  useEffect(() => {
    let cancelled = false
    if (!signedIn) {
      setLists([])
      setHydrated(true)
      return
    }
    setHydrated(false)
    ;(async () => {
      await refresh()
      if (!cancelled) setHydrated(true)
    })()
    return () => {
      cancelled = true
    }
  }, [signedIn, refresh])

  useEffect(() => {
    if (!signedIn) return
    client
      .query({ query: SHOPPING_UNITS_QUERY })
      .then(({ data }) => {
        setUnits(
          (data?.getUnitOfMesurement ?? [])
            .map((unit: any) => ({
              id: unit.unit_of_measurement_id,
              label: unit.unit_abbreviation || unit.unit_name || '',
              aliases: [unit.unit_name, unit.unit_abbreviation]
                .map((value) => (value ?? '').trim().toLowerCase())
                .filter(Boolean),
            }))
            .filter((unit: ShoppingUnitOption) => unit.label)
        )
      })
      .catch(() => {
        /* unit picker just stays empty */
      })
  }, [signedIn, client])

  const unitId = useCallback(
    (unit: string) => {
      const label = unit.trim().toLowerCase()
      if (!label) return 0 // 0 clears the unit on the server
      return units.find((entry) => entry.label.toLowerCase() === label)?.id ?? 0
    },
    [units]
  )

  const putList = useCallback((apiList: any) => {
    const next = fromApiList(apiList)
    setLists((current) =>
      current.some((list) => list.id === next.id)
        ? current.map((list) => (list.id === next.id ? next : list))
        : [next, ...current]
    )
    return next
  }, [])

  const putItem = useCallback((listId: string, apiItem: any) => {
    const next = fromApiItem(apiItem)
    setLists((current) =>
      current.map((list) => {
        if (list.id !== listId) return list
        const exists = list.items.some((item) => item.id === next.id)
        return touch({
          ...list,
          items: exists
            ? list.items.map((item) => (item.id === next.id ? next : item))
            : [...list.items, next],
        })
      })
    )
  }, [])

  const getList = useCallback(
    (id: string) => lists.find((list) => list.id === id),
    [lists]
  )

  const createList = useCallback(
    async (name: string) => {
      try {
        const { data } = await mutate(client, {
          mutation: CREATE_SHOPPING_LIST,
          variables: { name: name.trim() },
        })
        return putList(data.createShoppingList)
      } catch (error) {
        toast.error(errorMessage(error, 'Could not create the list'))
        return null
      }
    },
    [client, putList]
  )

  const renameList = useCallback(
    async (id: string, name: string) => {
      try {
        const { data } = await mutate(client, {
          mutation: RENAME_SHOPPING_LIST,
          variables: { listId: Number(id), name: name.trim() },
        })
        putList(data.renameShoppingList)
      } catch (error) {
        toast.error(errorMessage(error, 'Could not rename the list'))
      }
    },
    [client, putList]
  )

  const deleteList = useCallback(
    async (id: string) => {
      try {
        await mutate(client, {
          mutation: DELETE_SHOPPING_LIST,
          variables: { listId: Number(id) },
        })
        setLists((current) => current.filter((list) => list.id !== id))
      } catch (error) {
        toast.error(errorMessage(error, 'Could not delete the list'))
      }
    },
    [client]
  )

  const addItem = useCallback(
    async (
      listId: string,
      input: { name: string; quantity: string; unit: ShoppingUnit }
    ) => {
      const name = input.name.trim()
      if (!name) return false
      try {
        const { data } = await mutate(client, {
          mutation: ADD_SHOPPING_ITEM,
          variables: {
            input: {
              list_id: Number(listId),
              item_name: name,
              quantity: input.quantity.trim() || undefined,
              unit_of_measurement_id: unitId(input.unit) || undefined,
            },
          },
        })
        putItem(listId, data.addShoppingItem)
        return true
      } catch (error) {
        toast.error(errorMessage(error, 'Could not add the item'))
        return false
      }
    },
    [client, putItem, unitId]
  )

  const updateItem = useCallback(
    async (listId: string, itemId: string, patch: ItemPatch) => {
      const input: Record<string, unknown> = {}
      if (patch.name !== undefined) input.item_name = patch.name
      if (patch.quantity !== undefined) input.quantity = patch.quantity
      if (patch.unit !== undefined) input.unit_of_measurement_id = unitId(patch.unit)
      if (patch.category !== undefined) input.category = patch.category
      if (patch.purchased !== undefined) input.is_checked = patch.purchased
      try {
        const { data } = await mutate(client, {
          mutation: UPDATE_SHOPPING_ITEM,
          variables: { itemId: Number(itemId), input },
        })
        putItem(listId, data.updateShoppingItem)
      } catch (error) {
        toast.error(errorMessage(error, 'Could not update the item'))
      }
    },
    [client, putItem, unitId]
  )

  const togglePurchased = useCallback(
    async (listId: string, itemId: string) => {
      const current = lists
        .find((list) => list.id === listId)
        ?.items.find((item) => item.id === itemId)
      if (!current) return
      const purchased = !current.purchased
      // Optimistic: ticking an item should feel instant in the store.
      setLists((all) =>
        all.map((list) =>
          list.id === listId
            ? {
                ...list,
                items: list.items.map((item) =>
                  item.id === itemId ? { ...item, purchased } : item
                ),
              }
            : list
        )
      )
      try {
        await mutate(client, {
          mutation: UPDATE_SHOPPING_ITEM,
          variables: { itemId: Number(itemId), input: { is_checked: purchased } },
        })
      } catch (error) {
        toast.error(errorMessage(error, 'Could not update the item'))
        await refresh()
      }
    },
    [client, lists, refresh]
  )

  const removeItem = useCallback(
    async (listId: string, itemId: string) => {
      setLists((all) =>
        all.map((list) =>
          list.id === listId
            ? { ...list, items: list.items.filter((item) => item.id !== itemId) }
            : list
        )
      )
      try {
        await mutate(client, {
          mutation: REMOVE_SHOPPING_ITEM,
          variables: { itemId: Number(itemId) },
        })
      } catch (error) {
        toast.error(errorMessage(error, 'Could not remove the item'))
        await refresh()
      }
    },
    [client, refresh]
  )

  const clearPurchased = useCallback(
    async (listId: string) => {
      try {
        const { data } = await mutate(client, {
          mutation: CLEAR_SHOPPING_LIST_MARKS,
          variables: { listId: Number(listId) },
        })
        putList(data.clearShoppingListMarks)
      } catch (error) {
        toast.error(errorMessage(error, 'Could not reset the list'))
      }
    },
    [client, putList]
  )

  const moveItems = useCallback(
    async (fromListId: string, toListId: string, itemIds: string[]) => {
      if (fromListId === toListId || itemIds.length === 0) return true
      try {
        await mutate(client, {
          mutation: BULK_UPDATE_SHOPPING_ITEMS,
          variables: {
            input: {
              list_id: Number(fromListId),
              item_ids: itemIds.map(Number),
              move_to_list_id: Number(toListId),
            },
          },
        })
        // Both lists changed, so reload everything rather than patching two.
        await refresh()
        return true
      } catch (error) {
        toast.error(errorMessage(error, 'Could not move the items'))
        return false
      }
    },
    [client, refresh]
  )

  const toRedirectInput = (redirects?: RecipeRedirect[]) =>
    redirects && redirects.length
      ? redirects.map((r) => ({ recipe_ingredient_id: r.recipeIngredientId, item_id: r.itemId }))
      : undefined

  const addRecipeToList = useCallback(
    async (
      listId: string,
      recipe: RecipePayload,
      redirects?: RecipeRedirect[]
    ): Promise<AddRecipeResult> => {
      try {
        const { data } = await mutate(client, {
          mutation: ADD_RECIPE_TO_SHOPPING_LIST,
          variables: {
            listId: Number(listId),
            recipeId: Number(recipe.id),
            redirects: toRedirectInput(redirects),
          },
        })
        putList(data.addRecipeToShoppingList)
        // Items on the other lists changed too when something was redirected.
        if (redirects?.length) await refresh()
        return {
          ok: true,
          combined: countCombined(data.addRecipeToShoppingList, recipe.id),
          redirected: redirects?.length ?? 0,
        }
      } catch (error) {
        toast.error(errorMessage(error, 'Could not add the recipe'))
        return { ok: false, combined: 0, redirected: 0 }
      }
    },
    [client, putList, refresh]
  )

  const createListWithRecipe = useCallback(
    async (name: string, recipe: RecipePayload, redirects?: RecipeRedirect[]) => {
      try {
        const { data } = await mutate(client, {
          mutation: CREATE_SHOPPING_LIST_WITH_RECIPE,
          variables: {
            name: name.trim(),
            recipeId: Number(recipe.id),
            redirects: toRedirectInput(redirects),
          },
        })
        const list = putList(data.createShoppingListWithRecipe)
        if (redirects?.length) await refresh()
        return list
      } catch (error) {
        toast.error(errorMessage(error, 'Could not create the list'))
        return null
      }
    },
    [client, putList, refresh]
  )

  const getOverlaps = useCallback(
    async (recipeId: string, excludeListId?: string): Promise<ShoppingOverlap[]> => {
      try {
        const { data } = await client.query({
          query: SHOPPING_RECIPE_OVERLAPS,
          variables: {
            recipeId: Number(recipeId),
            excludeListId: excludeListId ? Number(excludeListId) : undefined,
          },
          fetchPolicy: 'network-only',
        })
        return (data?.shoppingRecipeOverlaps ?? []).map((o: any) => ({
          recipeIngredientId: o.recipe_ingredient_id,
          ingredientName: o.ingredient_name,
          itemId: o.item_id,
          itemName: o.item_name,
          itemQuantity: o.item_quantity ?? '',
          listId: String(o.list_id),
          listName: o.list_name,
          exact: !!o.exact,
        }))
      } catch {
        // The check is a convenience; if it fails, adding the recipe still works.
        return []
      }
    },
    [client]
  )

  const mergeItems = useCallback(
    async (listId: string, itemIds: string[]) => {
      try {
        const { data } = await mutate(client, {
          mutation: MERGE_SHOPPING_ITEMS,
          variables: { listId: Number(listId), itemIds: itemIds.map(Number) },
        })
        putList(data.mergeShoppingItems)
        return true
      } catch (error) {
        toast.error(errorMessage(error, 'Could not combine the items'))
        return false
      }
    },
    [client, putList]
  )

  const splitItem = useCallback(
    async (listId: string, itemId: string) => {
      try {
        const { data } = await mutate(client, {
          mutation: SPLIT_SHOPPING_ITEM,
          variables: { itemId: Number(itemId) },
        })
        putList(data.splitShoppingItem)
      } catch (error) {
        toast.error(errorMessage(error, 'Could not split the item'))
      }
    },
    [client, putList]
  )

  const dismissSuggestion = useCallback(
    async (listId: string, itemId: string, otherItemId: string) => {
      try {
        const { data } = await mutate(client, {
          mutation: DISMISS_SHOPPING_SUGGESTION,
          variables: { itemId: Number(itemId), otherItemId: Number(otherItemId) },
        })
        putList(data.dismissShoppingSuggestion)
      } catch (error) {
        toast.error(errorMessage(error, 'Could not save that'))
      }
    },
    [client, putList]
  )

  const resetQuantity = useCallback(
    async (listId: string, itemId: string) => {
      try {
        const { data } = await mutate(client, {
          mutation: UPDATE_SHOPPING_ITEM,
          variables: { itemId: Number(itemId), input: { reset_quantity: true } },
        })
        putItem(listId, data.updateShoppingItem)
      } catch (error) {
        toast.error(errorMessage(error, 'Could not reset the quantity'))
      }
    },
    [client, putItem]
  )

  const removeRecipe = useCallback(
    async (listId: string, recipeId: string, itemIds?: string[]) => {
      const list = lists.find((entry) => entry.id === listId)
      if (!list) return
      const linked = list.items.filter((item) => item.recipeIds.includes(recipeId))
      const chosen = itemIds ?? linked.map((item) => item.id)
      if (chosen.length === 0) return
      try {
        if (chosen.length >= linked.length) {
          const { data } = await mutate(client, {
            mutation: REMOVE_RECIPE_FROM_SHOPPING_LIST,
            variables: { listId: Number(listId), recipeId: Number(recipeId) },
          })
          putList(data.removeRecipeFromShoppingList)
        } else {
          // Partial selection: delete just those items, the recipe stays for the rest.
          const { data } = await mutate(client, {
            mutation: BULK_UPDATE_SHOPPING_ITEMS,
            variables: {
              input: {
                list_id: Number(listId),
                item_ids: chosen.map(Number),
                delete: true,
              },
            },
          })
          putList(data.bulkUpdateShoppingItems)
        }
      } catch (error) {
        toast.error(errorMessage(error, 'Could not remove the recipe'))
      }
    },
    [client, lists, putList]
  )

  const setSectionItems = useCallback(
    (listId: string, category: ShoppingCategory, items: ShoppingItem[]) => {
      draggingRef.current = true
      setLists((all) =>
        all.map((list) => {
          if (list.id !== listId) return list
          const next = items.map((item) => ({ ...item, category }))
          const ids = new Set(next.map((item) => item.id))
          // Keep every other item, dropping any that just moved into this section.
          const others = list.items.filter(
            (item) => item.category !== category && !ids.has(item.id)
          )
          return { ...list, items: [...others, ...next] }
        })
      )
    },
    []
  )

  const saveSectionOrder = useCallback(
    async (listId: string, category: ShoppingCategory) => {
      const list = listsRef.current.find((entry) => entry.id === listId)
      if (!list) {
        draggingRef.current = false
        return
      }
      const ids = list.items
        .filter((item) => item.category === category)
        .map((item) => Number(item.id))
      if (ids.length === 0) {
        draggingRef.current = false
        return
      }
      try {
        const { data } = await mutate(client, {
          mutation: REORDER_SHOPPING_ITEMS,
          variables: { input: { list_id: Number(listId), category, item_ids: ids } },
        })
        putList(data.reorderShoppingItems)
      } catch (error) {
        toast.error(errorMessage(error, 'Could not save the new order'))
        await refresh()
      } finally {
        draggingRef.current = false
      }
    },
    [client, putList, refresh]
  )

  const getInviteToken = useCallback(
    async (listId: string) => {
      try {
        const { data } = await mutate(client, {
          mutation: CREATE_SHOPPING_LIST_INVITE,
          variables: { listId: Number(listId) },
        })
        return (data?.createShoppingListInvite as string) ?? null
      } catch (error) {
        toast.error(errorMessage(error, 'Could not make the invite link'))
        return null
      }
    },
    [client]
  )

  const resetInviteToken = useCallback(
    async (listId: string) => {
      try {
        const { data } = await mutate(client, {
          mutation: RESET_SHOPPING_LIST_INVITE,
          variables: { listId: Number(listId) },
        })
        return (data?.resetShoppingListInvite as string) ?? null
      } catch (error) {
        toast.error(errorMessage(error, 'Could not reset the invite link'))
        return null
      }
    },
    [client]
  )

  const acceptInvite = useCallback(
    async (token: string, onSessionExpired?: () => void) => {
      try {
        const { data } = await mutate(client, {
          mutation: ACCEPT_SHOPPING_LIST_INVITE,
          variables: { token },
        })
        return putList(data.acceptShoppingListInvite)
      } catch (error) {
        // "Access Denied" means the login is missing or expired, not that the link is bad.
        if (onSessionExpired && (error as { message?: string })?.message === 'Access Denied') {
          onSessionExpired()
          return null
        }
        toast.error(errorMessage(error, 'Could not join this list'))
        return null
      }
    },
    [client, putList]
  )

  const removeMember = useCallback(
    async (listId: string, memberId: string) => {
      try {
        await mutate(client, {
          mutation: REMOVE_SHOPPING_LIST_MEMBER,
          variables: { listId: Number(listId), collaboratorId: Number(memberId) },
        })
        await refresh()
        return true
      } catch (error) {
        toast.error(errorMessage(error, 'Could not remove that person'))
        return false
      }
    },
    [client, refresh]
  )

  const totalItemCount = useMemo(
    () => lists.reduce((sum, list) => sum + list.items.length, 0),
    [lists]
  )

  const value = useMemo<ShoppingListsContextValue>(
    () => ({
      lists,
      hydrated,
      totalItemCount,
      units,
      refresh,
      getList,
      createList,
      renameList,
      deleteList,
      addItem,
      updateItem,
      togglePurchased,
      removeItem,
      clearPurchased,
      moveItems,
      addRecipeToList,
      createListWithRecipe,
      getOverlaps,
      mergeItems,
      splitItem,
      dismissSuggestion,
      resetQuantity,
      removeRecipe,
      setSectionItems,
      saveSectionOrder,
      getInviteToken,
      resetInviteToken,
      acceptInvite,
      removeMember,
    }),
    [
      lists,
      hydrated,
      totalItemCount,
      units,
      refresh,
      getList,
      createList,
      renameList,
      deleteList,
      addItem,
      updateItem,
      togglePurchased,
      removeItem,
      clearPurchased,
      moveItems,
      addRecipeToList,
      createListWithRecipe,
      getOverlaps,
      mergeItems,
      splitItem,
      dismissSuggestion,
      resetQuantity,
      removeRecipe,
      setSectionItems,
      saveSectionOrder,
      getInviteToken,
      resetInviteToken,
      acceptInvite,
      removeMember,
    ]
  )

  return (
    <ShoppingListsContext.Provider value={value}>
      {children}
    </ShoppingListsContext.Provider>
  )
}

export function useShoppingLists() {
  const context = useContext(ShoppingListsContext)
  if (!context) {
    throw new Error('useShoppingLists must be used within ShoppingListsProvider')
  }
  return context
}

export function useOptionalShoppingLists() {
  return useContext(ShoppingListsContext)
}

export function recipeLinkedCount(list: ShoppingList, recipeId: string) {
  return list.items.filter((item) => item.recipeIds.includes(recipeId)).length
}

export function quantityLabel(item: ShoppingItem) {
  return formatQuantity(item.quantity, item.unit)
}
